import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, ExternalLink, Info, TriangleAlert } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useIncidents } from '@/hooks/useIncidents';
import { useDeviceTrafficSeries } from '@/hooks/useDeviceTrafficSeries';
import { type InterfaceSummary, useDeviceTrafficSummary } from '@/hooks/useDeviceTrafficSummary';
import { buildRows, hasQuantity, portName, summariseCapacity } from './rows';
import { denominatorNote } from './capacity';
import { formatBucket } from './format';
import { RangeControl } from './RangeControl';
import { RANGE_MS, refreshMs, type TrafficRange } from './range';
import { VerdictStrip } from './VerdictStrip';
import { TrafficMirror } from './TrafficMirror';
import { FaultFacets } from './FaultFacets';
import { EventRail } from './EventRail';
import { PortRanking } from './PortRanking';
import { PortLedger } from './PortLedger';
import { COMPARE_LIMIT, ComparePorts, type ComparePort } from './ComparePorts';

const SYNC_ID = 'device-traffic-tab';

/**
 * Everything this device's ports carried, at full page height.
 *
 * The default range is 24h rather than the panels' 1h: arriving here is a
 * review, while a panel is opened by someone who just clicked a graph and
 * wants the last hour.
 *
 * The blocks are the same components the three Views panels use — that was the
 * point of building the interface panel first. What this surface adds is the
 * complete ledger, the comparison, and the room to show them.
 */
export function DeviceTrafficTab({
  deviceId,
  deviceType,
  incidentId,
}: {
  deviceId: string;
  deviceType: string | null;
  /** Arrived from an incident: its window frames everything below. */
  incidentId?: string | null;
}) {
  const [range, setRange] = useState<TrafficRange>('24h');
  const [unit, setUnit] = useState<'bits' | 'percent'>('bits');
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  // Fixed-length slots rather than a list, so unticking a port leaves every
  // other port's hue exactly where it was.
  const [slots, setSlots] = useState<(string | null)[]>(() => Array<string | null>(COMPARE_LIMIT).fill(null));
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => { setNow(Date.now()); }, refreshMs(range));
    return () => { clearInterval(id); };
  }, [range]);
  const bounds = useMemo(() => ({ start: now - RANGE_MS[range], end: now }), [now, range]);

  const summary = useDeviceTrafficSummary(deviceId, bounds);
  const { user } = useAuth();
  const { data: incidents } = useIncidents(user?.organizationId ?? undefined, { deviceId });

  // Row one of the ranking, until the reader picks otherwise. The worst port is
  // what someone arriving at this page came to look at.
  const ranked = useMemo(() => (summary.data?.interfaces ?? []).filter((row) => !row.aggregated), [summary.data]);
  // `ranked.at(0)` rather than `ranked[0]`: the project does not run
  // `noUncheckedIndexedAccess`, so an index types as present even on an empty
  // list, and a device with no ports would focus on `undefined.interfaceId`.
  const focusId = selectedId ?? ranked.at(0)?.interfaceId;
  const focus = ranked.find((row) => row.interfaceId === focusId);

  const series = useDeviceTrafficSeries(focusId === undefined ? undefined : deviceId, bounds, focusId);
  const iface = series.data?.interfaces.find((row) => row.interfaceId === focusId);
  const rows = useMemo(() => (iface ? buildRows(iface) : []), [iface]);
  const capacity = useMemo(() => summariseCapacity(iface?.capacity ?? []), [iface]);
  const note = denominatorNote(capacity, null);

  if (!note.available && unit !== 'bits') setUnit('bits');

  const compared: ComparePort[] = slots.flatMap((interfaceId, slot) => {
    if (interfaceId === null) return [];
    const row = (summary.data?.interfaces ?? []).find((candidate) => candidate.interfaceId === interfaceId);
    return [{ interfaceId, interfaceName: row ? portName(row) : 'a port', slot }];
  });
  const comparing = compared.length > 0;

  const toggleCompare = (row: InterfaceSummary) => {
    setSlots((previous) => {
      const existing = previous.indexOf(row.interfaceId);
      if (existing >= 0) {
        const next = [...previous];
        next[existing] = null;
        return next;
      }
      const free = previous.indexOf(null);
      // The eighth tick does nothing rather than silently generating a hue, and
      // the control says why.
      if (free < 0) return previous;
      const next = [...previous];
      next[free] = row.interfaceId;
      return next;
    });
  };

  const caption = [
    series.data?.bucketSeconds !== null && series.data?.bucketSeconds !== undefined && series.data.bucketFunction
      ? `Each point is the ${series.data.bucketFunction === 'min' ? 'lowest' : 'highest'} reading in ${formatBucket(series.data.bucketSeconds)}.`
      : null,
    note.caption,
  ]
    .filter((part): part is string => part !== null)
    .join(' ');

  if (summary.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (summary.isError || !summary.data) {
    return (
      <section className="bg-card border border-border rounded-lg p-5">
        <p className="text-sm text-muted-foreground">
          The metric store did not answer. The device itself is unaffected.
        </p>
      </section>
    );
  }

  const verdict = summary.data.verdict;

  return (
    <div className="space-y-5">
      {/* One filter row, scoping everything below it. */}
      <section className="bg-card border border-border rounded-lg p-4 flex items-center gap-3 flex-wrap">
        <RangeControl value={range} onChange={setRange} />
        <Segmented
          ariaLabel="Units"
          value={unit}
          onChange={setUnit}
          options={[
            { value: 'bits' as const, label: 'bits/s' },
            { value: 'percent' as const, label: '% of line rate', disabled: !note.available },
          ]}
          disabledReason={note.reason}
        />
        <Segmented
          ariaLabel="Direction"
          value={direction}
          onChange={setDirection}
          options={[
            { value: 'in' as const, label: 'in', disabled: !comparing },
            { value: 'out' as const, label: 'out', disabled: !comparing },
          ]}
          disabledReason="the mirror already shows both directions; tick ports to compare one"
        />
        <span className="ml-auto text-[11px] text-muted-foreground">
          above the line: into this port · below: out of it
        </span>
        <Link
          to={`/views?device=${deviceId}&layer=throughput&range=${range}`}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          View in topology
          <ExternalLink className="w-3 h-3" />
        </Link>
      </section>

      {incidentId ? (
        <p className="text-xs text-muted-foreground">
          Arrived from{' '}
          <Link to={`/incidents/${incidentId}`} className="underline underline-offset-2">
            an incident
          </Link>
          . The range control above is showing the last {range}, not the incident window.
        </p>
      ) : null}

      <section className="bg-card border border-border rounded-lg p-5">
        <h2 className="text-sm font-semibold mb-4">Across all {String(verdict.portsTotal)} ports</h2>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <Tile label="Ports losing frames" value={String(verdict.portsWithFaults)} detail={`of ${String(verdict.portsRanked)} ranked`} tone={verdict.portsWithFaults > 0} />
          <Tile label="Ports flapping" value={String(verdict.portsFlapping)} detail="link state changes" tone={verdict.portsFlapping > 0} />
          <Tile label="Worst" value={verdict.worstInterface ?? '—'} detail={verdict.worstInterface === null ? 'nothing faulting' : 'by frames lost'} tone={verdict.worstInterface !== null} />
          <Tile label="Busiest" value={verdict.busiestInterface ?? '—'} detail="by peak rate" tone={false} />
        </div>
        <p className="mt-3 flex items-start gap-2 text-sm text-foreground">
          <VerdictIcon severity={verdict.severity} />
          <span>{verdict.sentence}</span>
        </p>
      </section>

      <section className="bg-card border border-border rounded-lg p-5">
        <PortRanking
          rows={summary.data.interfaces}
          totalRanked={verdict.portsRanked}
          onSelect={(row) => { setSelectedId(row.interfaceId); }}
        />
      </section>

      {focus ? (
        <section className="bg-card border border-border rounded-lg p-5">
          <header className="mb-4 flex items-baseline justify-between gap-3 flex-wrap">
            <h2 className="text-sm font-semibold">
              {comparing ? 'Comparison' : portName(focus)}
              {!comparing && selectedId === null ? (
                <span className="ml-2 font-normal text-muted-foreground">worst port, by the ranking above</span>
              ) : null}
            </h2>
            {comparing ? (
              <button
                type="button"
                onClick={() => { setSlots(Array<string | null>(COMPARE_LIMIT).fill(null)); }}
                className="text-[11px] text-muted-foreground hover:text-foreground"
              >
                Clear comparison
              </button>
            ) : null}
          </header>

          {/* The row's own numbers stand in until the port's series lands, so
              the strip does not flash empty on every selection change. */}
          {!comparing ? <VerdictStrip verdict={iface?.verdict ?? placeholder(focus)} /> : null}

          <div className="mt-5 space-y-5">
            {/* The mirror and the comparison swap rather than stack: this is the
                one block where `--series-1` means an interface rather than
                "inbound". */}
            {comparing ? (
              <ComparePorts
                deviceId={deviceId}
                ports={compared}
                start={bounds.start}
                end={bounds.end}
                direction={direction}
                syncId={SYNC_ID}
              />
            ) : iface ? (
              <TrafficMirror
                rows={rows}
                start={bounds.start}
                end={bounds.end}
                syncId={SYNC_ID}
                capacity={capacity}
                caption={caption}
                mode={unit}
              />
            ) : (
              <Skeleton className="h-56 w-full" />
            )}

            {iface ? (
              <>
                <EventRail
                  start={bounds.start}
                  end={bounds.end}
                  syncId={SYNC_ID}
                  statusBefore={iface.statusBefore}
                  transitions={iface.transitions}
                  incidents={incidents ?? []}
                />
                <FaultFacets
                  rows={rows}
                  start={bounds.start}
                  end={bounds.end}
                  syncId={SYNC_ID}
                  hasErrors={hasQuantity(iface, 'errors_in') || hasQuantity(iface, 'errors_out')}
                  hasDiscards={hasQuantity(iface, 'discards_in') || hasQuantity(iface, 'discards_out')}
                  hasPacketMix={hasQuantity(iface, 'nonunicast_in') && hasQuantity(iface, 'packets_in')}
                />
              </>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="bg-card border border-border rounded-lg p-5">
        <PortLedger
          rows={summary.data.interfaces}
          onSelect={(row) => { setSelectedId(row.interfaceId); }}
          compare={{
            selected: slots.filter((value): value is string => value !== null),
            limit: COMPARE_LIMIT,
            onToggle: toggleCompare,
          }}
        />
      </section>

      {deviceType !== null && deviceType.toLowerCase().includes('router') ? (
        <section className="bg-card border border-border rounded-lg p-5">
          <h2 className="text-sm font-semibold mb-2">IP layer</h2>
          <p className="text-sm text-muted-foreground">
            Of the datagrams this router received, what happened to them — delivered, forwarded, or dropped for a header
            error, an address error or no route. Not built yet: the counters exist on three or four of the fleet&apos;s
            devices and every query over them has to split by address family, or IPv4 and IPv6 are differenced together
            into alternating spikes.
          </p>
        </section>
      ) : null}
    </div>
  );
}

/** A verdict for a port whose series has not arrived yet, from the row we do have. */
function placeholder(row: InterfaceSummary) {
  return {
    rule: 'clean',
    sentence: row.reason,
    severity: 'none' as const,
    peakBitsIn: row.bitsIn,
    peakBitsOut: row.bitsOut,
    peakUtilization: row.utilization,
    peakErrorsIn: null,
    peakErrorsOut: null,
    peakDiscardsIn: null,
    peakDiscardsOut: null,
    nonUnicastShare: null,
    operStatus: row.operStatus,
    flaps: row.flaps,
    totalErrorsIn: row.errorsIn,
    totalErrorsOut: row.errorsOut,
    totalDiscardsIn: row.discardsIn,
    totalDiscardsOut: row.discardsOut,
  };
}

function Tile({ label, value, detail, tone }: { label: string; value: string; detail: string; tone: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-1.5">
        {tone ? <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0 bg-sev-3" aria-hidden="true" /> : null}
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
      </div>
      <p className="text-2xl font-semibold mt-1 truncate">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{detail}</p>
    </div>
  );
}

function VerdictIcon({ severity }: { severity: string }) {
  if (severity === 'critical') return <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5 text-sev-1" aria-label="critical" />;
  if (severity === 'warning') return <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-sev-3" aria-label="warning" />;
  return <Info className="w-4 h-4 shrink-0 mt-0.5 text-muted-foreground" aria-label="note" />;
}

function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  disabledReason,
}: {
  options: { value: T; label: string; disabled?: boolean }[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  disabledReason?: string | null;
}) {
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden" role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          disabled={option.disabled}
          title={option.disabled === true ? (disabledReason ?? undefined) : undefined}
          onClick={() => { onChange(option.value); }}
          aria-pressed={value === option.value}
          className={cn(
            'px-2.5 py-1 text-xs border-r border-border last:border-r-0',
            value === option.value ? 'bg-muted text-foreground font-medium' : 'text-muted-foreground hover:text-foreground',
            option.disabled === true && 'opacity-40 cursor-not-allowed hover:text-muted-foreground',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
